import { TTheme } from '@/core/theme'
import Typography from '@/ui/components/typography'
import { styled } from '@mui/system'

export const Container = styled('div')<{ theme?: TTheme }>`
    position: relative;
    padding: 20px 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 20px 0;
`

export const Header = styled('div')`
    display: flex;
    align-items: center;
    justify-content: space-between;
`

export const Title = styled('h1')<{ theme?: TTheme }>`
    font-size: var(--font-size-xl);
    font-weight: 600;
    color: ${({ theme }) => theme?.colors.greys[50]};
`

export const SectionHeader = styled('div')`
    padding-bottom: 16px;
`

export const SectionHeaderContent = styled('div')`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 6px;
    border-bottom: 1px solid var(--color-greys-200);
    padding-bottom: 10px;

    .icon {
        margin-top: 4px;
    }
`

export const SectionTitle = styled(Typography)<{ theme?: TTheme }>`
    color: ${({ theme }) => theme?.colors.greys[800]};
`

export const TabsContainer = styled('div')`
    width: 100%;
`

export const TabList = styled('div')<{ theme: TTheme }>`
    display: flex;
    gap: 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.greys[200]};
    margin-bottom: 24px;
`

export const Tab = styled('button')<{
    active?: boolean
    theme: TTheme
}>`
    padding: 8px 16px;
    font-size: var(--font-size-s);
    font-weight: 500;
    color: ${({ active, theme }) =>
        active ? theme.colors.greys[50] : theme.colors.greys[500]};
    border-bottom: 2px solid
        ${({ active, theme }) =>
            active ? theme.colors.greys[50] : 'transparent'};
    background: none;
    border-top: none;
    border-left: none;
    border-right: none;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        color: ${({ theme }) => theme.colors.greys[50]};
    }
`

export const TimeRangeContainer = styled('div')<{ theme?: TTheme }>`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 32px;
    color: var(--color-greys-800);

    .custom-dropdown {
        button {
            span {
                font-size: 14px !important;
            }
            color: ${({ theme }) => theme.colors.primary[600]};
        }
    }
`

export const TimeRangeSelect = styled('select')<{
    theme?: TTheme
}>`
    padding: 8px 12px;
    border: 1px solid ${({ theme }) => theme?.colors.greys[200]};
    border-radius: 4px;
    background: ${({ theme }) => theme?.colors.greys[50]};
    color: ${({ theme }) => theme?.colors.primary[900]};
    cursor: pointer;
    font-size: var(--font-size-s);

    &:hover {
        border-color: ${({ theme }) => theme?.colors.primary[300]};
    }
`

export const Grid = styled('div')`
    display: grid;
    gap: 24px;
    margin-bottom: 24px;
`

export const TopGrid = styled(Grid)`
    grid-template-columns: 1fr 2fr;
`

export const BottomGrid = styled(Grid)`
    grid-template-columns: 2fr 1.3fr;
`

export const Card = styled('div')<{ theme?: TTheme }>`
    background: ${({ theme }) => theme.colors.semantic.backgroundSecondary};
    color: ${({ theme }) => theme?.colors.greys[50]};
    border-radius: 4px;
    padding: 20px;
    box-shadow: 5px 5px 15px 0px rgba(0, 0, 0, 0.05);
`

export const CardHeader = styled('div')`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
`

export const CardTitle = styled('h2')<{ theme?: TTheme }>`
    font-size: var(--font-size-xl);
    font-weight: 600;
    color: ${({ theme }) => theme?.colors.greys[800]};
    margin: 0 0 24px 0;
`

export const MetricContainer = styled('div')`
    display: flex;
    align-items: center;
    gap: 16px;
`

export const MetricHeader = styled('div')`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`

export const IconWrapper = styled('div')<{ theme?: TTheme }>`
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme?.colors.greys[600]};
    display: flex;
    align-items: center;
    justify-content: center;
`

export const MetricValue = styled('div')<{ theme?: TTheme }>`
    font-size: var(--font-size-xxl);
    font-weight: 600;
    color: ${({ theme }) => theme?.colors.greys[800]};
    line-height: normal;
    margin-bottom: 6px;
`

export const MetricLabel = styled('div')<{ theme?: TTheme }>`
    font-size: var(--font-size-m);
    font-weight: 500;
    color: ${({ theme }) => theme?.colors.greys[800]};
`

export const SeverityList = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 24px;
`

export const SeverityItem = styled('div')`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--font-size-s);
`

export const SeverityCount = styled('span')<{ theme?: TTheme }>`
    color: ${({ theme }) => theme?.colors.greys[50]};
    font-weight: 500;
    margin-right: 4px;
`

export const SeverityLabel = styled('span')<{ theme?: TTheme }>`
    color: ${({ theme }) => theme?.colors.greys[400]};
`

export const IncidentMetrics = styled('div')`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 15px;
    min-height: 218px;
`

export const IncidentMetric = styled('div')<{
    variant: 'all' | 'closed' | 'inProgress' | 'waiting'
    theme?: TTheme
}>`
    padding: 16px;
    border-radius: 8px;
    background: ${({ theme, variant }) => {
        switch (variant) {
            case 'waiting':
                return theme?.colors.semantic.caseLightRed
            case 'inProgress':
                return theme?.colors.greys[75]
            case 'closed':
                return theme?.colors.semantic.caseLightGreen
            case 'all':
            default:
                return theme?.colors.greys[75]
        }
    }};
    border: 1px solid
        ${({ theme, variant }) => {
            switch (variant) {
                case 'waiting':
                    return theme?.colors.semantic.caseLightRedBorder

                case 'inProgress':
                    return theme?.colors.greys[200]
                case 'closed':
                    return theme?.colors.semantic.caseLightGreenBorder
                case 'all':
                default:
                    return theme?.colors.greys[200]
            }
        }};
`

export const MetricDescription = styled('div')<{ theme?: TTheme }>`
    font-size: var(--font-size-s);
    color: ${({ theme }) => theme?.colors.greys[500]};
    line-height: 1.4;
`

export const ViewIncidentButton = styled(Typography)<{ theme?: TTheme }>`
    color: ${({ theme }) => theme?.colors.semantic.error};
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }

    .icon {
        margin-right: 6px;
    }
`

export const ChartContainer = styled('div')`
    height: 140px;
    width: 140px;
    position: relative;
`

export const AlertsContainer = styled('div')`
    display: flex;
    gap: 24px;
    align-items: center;
`

export const AlertsHeader = styled('div')`
    display: flex;
    justify-content: space-between;
    align-items: center;
`

export const AlertCount = styled('div')<{ theme?: TTheme }>`
    font-size: var(--font-size-m)
    font-weight: 500;
    color: ${({ theme }) => theme?.colors.greys[800]};
`

export const AlertsList = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

export const AlertItem = styled('div')`
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
`

export const AlertDot = styled('div')<{
    severity: TCaseSeverity
    theme?: TTheme
}>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${({ severity, theme }) => {
        switch (severity) {
            case 'critical':
                return theme.colors.semantic.alertCritical
            case 'high':
                return theme.colors.semantic.alertHigh
            case 'medium':
                return theme.colors.semantic.alertMedium
            case 'low':
                return theme.colors.semantic.alertLow
            case 'informational':
                return theme.colors.semantic.alertInfo
            default:
                return theme.colors.semantic.alertInfo
        }
    }};
`

export const AlertValue = styled('span')<{ theme?: TTheme }>`
    font-size: var(--font-size-s);
    font-weight: 500;
    color: ${({ theme }) => theme?.colors.greys[800]};
    min-width: 25px;
`

export const AlertLabel = styled('span')<{ theme?: TTheme }>`
    font-size: var(--font-size-s);
    color: ${({ theme }) => theme?.colors.greys[600]};
`

export const StyledText = styled(Typography)<{ theme?: TTheme }>`
    color: ${({ theme }) => theme?.colors.greys[800]};
`

export const DemoOverlay = styled('div')<{
    theme?: TTheme
    isFading?: boolean
}>`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme?.colors.semantic.mdrDemoOverlay};
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(1px);
    opacity: ${({ isFading }) => (isFading ? 0 : 1)};
    transition: opacity 0.5s ease-out;
`

export const DemoModal = styled('div')<{ theme?: TTheme; isFading?: boolean }>`
    background: ${({ theme }) => theme?.colors.semantic.backgroundSecondary};
    border-radius: 4px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
    padding: 40px 48px;
    display: flex;
    flex-direction: column;
    align-items: center;
    transform: translateY(-270px);
    opacity: ${({ isFading }) => (isFading ? 0 : 1)};
    transition: opacity 0.5s ease-out;
`

export const DemoModalTitle = styled(Typography)<{ theme?: TTheme }>`
    margin-bottom: 24px;
    color: ${({ theme }) => theme?.colors.greys[900]};
    text-align: center;
`

export const DemoModalButton = styled('button')<{ theme?: TTheme }>`
    background: ${({ theme }) => theme?.colors.primary[400]};
    color: ${({ theme }) => theme?.colors.greys[0]};

    &:hover {
        background: ${({ theme }) => theme?.colors.primary[500]};
    }

    border: none;
    border-radius: 8px;
    padding: 12px 32px;
    font-size: var(--font-size-m);
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.08);
`
